import {useEffect, useState, useRef} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Form from "react-bootstrap/Form";

import NavBreadcrum from "components/NavBreadcrum";
import {SwalAlertClass} from "core/swalClass";
import axiApi from "ApiCaller";
import TableParams from "./TableParams";
import {DeviceAlertModel} from "site-apps/devices/configs/DeviceAlertModel";
import navs from "../navs";
import FormButton from "components/buttons/FormButton";
import useCustomTranslation from "core/customTranslation";
import translations from "./translations";
import {MiniQConfigAlertsValues, MiniQConfigAlertsHas} from "./models/form/models/MiniQConfigAlerts";
import {MiniQ24GConfigAlertsValues, MiniQ24GConfigAlertsHas} from "./models/form/models/MiniQ24GConfigAlerts";

const MODELS_SELECTOR = {
    "MQ-SX": {
        has: MiniQConfigAlertsHas,
        values: MiniQConfigAlertsValues,
    },
    "MQ2-4G": {
        has: MiniQ24GConfigAlertsHas,
        values: MiniQ24GConfigAlertsValues,
    },
    getHas: function (model) {
        return this[model]?.has;
    },
    getValues: function (model) {
        return this[model]?.values;
    },
};

const FormAlertSetup = () => {
    const {t: tR} = useTranslation("routes");
    const {t: _TC} = useCustomTranslation(translations);

    document.title = tR("labels.alerts-configuration");
    const navigate = useNavigate();
    const Alert = new SwalAlertClass({confirmButtonText: _TC("button.accept")});

    const bread = [
        {name: tR("labels.device-configuration"), url: "/device-configuration/", current: false},
        {name: tR("configuration.alerts"), url: "/device-configuration/alerts/", current: false},
        {name: tR("labels.alerts-configuration"), current: true},
    ];
    const formRef = useRef(null);

    const [item, setItem] = useState({...DeviceAlertModel});
    const [models, setModels] = useState([]);
    const modes = useRef({});

    const {id} = useParams();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let idNumber = parseInt(id);
        if (isNaN(idNumber)) {
            axiApi.get("/api/devices/configs/alerts/modes/").then((resp) => {
                if (resp.data.status) {
                    modes.current = {...resp.data.modes};
                    setModels([...resp.data.models]);
                    let item_ = {...DeviceAlertModel};
                    item_.model = resp.data.models[0];
                    item_.has = {...resp.data.modes.has};
                    item_.values = {...resp.data.modes.values};
                    setItem(item_);
                }
            });
        } else {
            axiApi
                .get(`/api/devices/configs/alerts/alert/${idNumber}/`)
                .then((r) => {
                    if (r.data.status) {
                        setItem({...r.data.item});
                        setModels([...r.data.models]);
                        let modes_ = r.data.modes;
                        modes_[r.data.item.model].has =
                            Object.keys(r.data.item.has).length > 0 ? r.data.item.has : MODELS_SELECTOR.getHas(r.data.item.model);
                        modes_[r.data.item.model].values =
                            Object.keys(r.data.item.values).length > 0
                                ? r.data.item.values
                                : MODELS_SELECTOR.getValues(r.data.item.model);
                        modes.current = {...modes_};
                    } else {
                        r.data.message && console.info(r.data.message);
                        Alert.warning(_TC("message.error.service"), _TC("message.error.retrieve"));
                    }
                })
                .catch((err) => {
                    console.error(err);
                    if (err.response.status === 404) {
                        Alert.error(_TC("message.error.server"), _TC("message.error.id"));
                    } else {
                        Alert.error(_TC("message.error.server"), _TC("message.error.admin"));
                    }
                });
        }
    }, [id]);

    const onSubmit = (e) => {
        setIsLoading(true);
        let form = new FormData(formRef.current);
        let name = form.get("name");
        if (!name) {
            Alert.error(_TC("message.error.required.name"));
            setIsLoading(false);
            return;
        }
        axiApi.post("/api/devices/configs/alerts/alert/", form).then((r) => {
            if (r.data.status) {
                Alert.success(_TC("message.success.title"), _TC("message.success.text")).then(() => {
                    navigate("/device-configuration/alerts/");
                });
            } else {
                r.data.message && console.log(r.data.message);
                Alert.warning(_TC("message.error.save"), _TC("message.error.admin"));
            }
            setIsLoading(false);
        });
    };

    const changeModel = (e) => {
        let model_ = e.target.value;
        modes.current[item.model].has = {...item.has};
        modes.current[item.model].values = {...item.values};
        setItem({...item, model: model_});
    };

    return (
        <>
            <NavBreadcrum navs={navs} bread={bread} />
            <div className="preview-canvas mt-1">
                <div className="preview-icon">
                    <FontAwesomeIcon icon="bell" />
                </div>
                <div className="preview-title d-flex align-items-center ms-3">
                    <h3>{item.name ? item.name : _TC("create.title")}</h3>
                </div>
            </div>
            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                }}
                ref={formRef}>
                <input type="hidden" name="id" defaultValue={item.id} />
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-1 col-md-2">
                            <div className="text-md-end mt-2">
                                <label htmlFor="name" className="form-label">
                                    {_TC("name")}
                                </label>
                            </div>
                        </div>
                        <div className="col-lg-5 col-12 d-flex align-items-center">
                            <input
                                type="text"
                                id="name"
                                name="name"
                                className="form-control"
                                required={true}
                                defaultValue={item.name}
                                autoComplete="off"
                            />
                        </div>
                        <div className="col-lg-1 col-md-2">
                            <div className="text-md-end mt-2">
                                <label htmlFor="model" className="form-label">
                                    {_TC("model")}
                                </label>
                            </div>
                        </div>
                        <div className="col-lg-3 col-12 d-flex align-items-center">
                            <Form.Select
                                className="mb-2"
                                id="model"
                                name="model"
                                value={item.model}
                                style={{maxWidth: "120px"}}
                                onChange={changeModel}>
                                {models.map((model, index) => (
                                    <option key={index} value={model}>
                                        {model}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-lg-1 col-md-3">
                            <div className="text-md-end mt-2">
                                <label htmlFor="description" className="form-label">
                                    {_TC("description")}
                                </label>
                            </div>
                        </div>
                        <div className="col-lg-7 col-md-8 col-12 d-flex align-items-center">
                            <textarea
                                id="description"
                                name="description"
                                className="form-control"
                                defaultValue={item.description}
                            />
                        </div>
                    </div>
                    <div className="row mt-3 mb-3">
                        <TableParams item={item} setItem={setItem} modes={modes} _TC={_TC} />
                    </div>
                </div>

                <div className="fixed-bottom d-flex justify-content-end p-3 bg-light">
                    <button
                        type="button"
                        className="btn btn-secondary me-4"
                        onClick={() => navigate("/device-configuration/alerts/")}>
                        {_TC("button.cancel")}
                    </button>
                    <FormButton type="button" onClick={onSubmit} className="btn btn-primary" isProcessing={isLoading}>
                        {_TC("button.save")}
                    </FormButton>
                </div>
            </Form>
        </>
    );
};

export default FormAlertSetup;
